/**
 * HVP Update Scheduler
 *
 * Two-phase update system to prevent layout thrashing and ensure smooth 60fps performance.
 *
 * Phase 1 (DOM Updates):
 * - Executes all queued DOM updates sorted by priority
 * - Uses requestAnimationFrame for batching
 *
 * Phase 2 (UI Updates):
 * - Waits for browser paint (another requestAnimationFrame)
 * - Executes UI state changes (highlighting, visibility)
 *
 * This separation prevents read/write/read/write patterns that cause
 * multiple forced reflows.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

import { applyMixin } from './mixin.js';
import { SchedulerFlushMethods } from './scheduler-flush.js';

export class HVPUpdateScheduler {
    /**
     * Constructor
     *
     * @param {Object} options - Configuration options
     * @param {boolean} options.debug - Enable debug logging
     */
    constructor(options = {}) {
        this.debug = options.debug || false;

        // Priority values (lower = higher priority)
        this.PRIORITY = {
            critical: 0,
            high: 1,
            normal: 2,
            low: 3
        };

        // DOM update queue: [{ fn, priority, description }]
        this.domUpdates = [];

        // UI update queue: Set of { type, source, callback }
        this.uiUpdates = new Set();

        // State
        this.isFlushing = false;
        this.flushScheduled = false;

        // Statistics
        this.stats = {
            domUpdateCount: 0,
            uiUpdateCount: 0,
            flushCount: 0,
            totalFlushTime: 0,
            lastFlushTime: 0
        };

        // Event callbacks
        this.onUIUpdate = null;
    }

    /**
     * Schedule a DOM update with priority
     *
     * DOM updates are batched and executed in priority order during Phase 1.
     *
     * @param {Function} fn - The update function to execute
     * @param {Object} options - Options
     * @param {string} options.priority - Priority level: 'critical', 'high', 'normal', 'low'
     * @param {string} options.description - Description for debugging
     * @returns {Promise} Resolves when the update is executed
     */
    scheduleDOMUpdate(fn, options = {}) {
        const {
            priority = 'normal',
            description = 'DOM Update'
        } = options;

        return new Promise((resolve, reject) => {
            this.domUpdates.push({
                fn,
                priority: this.PRIORITY[priority] ?? this.PRIORITY.normal,
                description,
                resolve,
                reject
            });

            this.log(`Scheduled DOM update: ${description} (priority: ${priority})`);
            this.scheduleFlush();
        });
    }

    /**
     * Schedule a UI update (batched)
     *
     * UI updates are deduplicated by type and executed during Phase 2.
     *
     * @param {string} type - Update type: 'highlight', 'visibility', 'progress', 'all'
     * @param {string} source - Source of the update (for debugging)
     * @param {Function} callback - Optional callback to execute
     */
    scheduleUIUpdate(type, source = 'unknown', callback = null) {
        // Create a unique key for deduplication
        const key = `${type}:${source}`;

        // Remove existing update of same type/source
        for (const update of this.uiUpdates) {
            if (`${update.type}:${update.source}` === key) {
                this.uiUpdates.delete(update);
                break;
            }
        }

        this.uiUpdates.add({ type, source, callback });
        this.log(`Scheduled UI update: ${type} (source: ${source})`);
        this.scheduleFlush();
    }

    /**
     * Schedule a flush if not already scheduled
     */
    scheduleFlush() {
        if (this.flushScheduled || this.isFlushing) {
            return;
        }

        this.flushScheduled = true;
        requestAnimationFrame(() => this.flush());
    }

    /**
     * Register callback for UI updates
     *
     * @param {Function} callback - Callback function
     */
    setUIUpdateCallback(callback) {
        this.onUIUpdate = callback;
    }

    /**
     * Schedule DOM update and wait for completion
     *
     * Convenience method that schedules an update and returns when complete.
     *
     * @param {Function} fn - The update function
     * @param {Object} options - Options
     * @returns {Promise} Resolves with the function result
     */
    async scheduleAndWait(fn, options = {}) {
        return this.scheduleDOMUpdate(fn, options);
    }

    /**
     * Clear all pending updates
     *
     * Safely cancels pending updates without causing unhandled rejections.
     */
    clear() {
        // Safely reject pending DOM updates
        // Wrap in try-catch to prevent unhandled promise rejections
        this.domUpdates.forEach(({ reject, description }) => {
            try {
                // Create a specific error type that can be identified
                const cancelError = new Error(`Update cancelled: ${description}`);
                cancelError.name = 'CancelledError';
                reject(cancelError);
            } catch (e) {
                // Ignore rejection errors - update was already handled
            }
        });

        this.domUpdates = [];
        this.uiUpdates.clear();
        this.log('All pending updates cleared');
    }

    /**
     * Check if scheduler is busy
     *
     * @returns {boolean}
     */
    isBusy() {
        return this.isFlushing || this.domUpdates.length > 0 || this.uiUpdates.size > 0;
    }

    /**
     * Get statistics
     *
     * @returns {Object}
     */
    getStats() {
        return {
            ...this.stats,
            pendingDOMUpdates: this.domUpdates.length,
            pendingUIUpdates: this.uiUpdates.size,
            isFlushing: this.isFlushing,
            avgFlushTime: this.stats.flushCount > 0
                ? (this.stats.totalFlushTime / this.stats.flushCount).toFixed(2) + 'ms'
                : '0ms'
        };
    }

    /**
     * Reset statistics
     */
    resetStats() {
        this.stats = {
            domUpdateCount: 0,
            uiUpdateCount: 0,
            flushCount: 0,
            totalFlushTime: 0,
            lastFlushTime: 0
        };
    }

    /**
     * Log message (debug mode only)
     */
    log(...args) {
        if (this.debug) {
            window.hvpData && window.hvpData.debug && console.log(
                '%c[HVP Scheduler]',
                'background: #8b5cf6; color: #fff; padding: 2px 6px; border-radius: 3px;',
                ...args
            );
        }
    }
}

// The two-phase flush lives in a sibling module (350-line rule).
applyMixin(HVPUpdateScheduler.prototype, SchedulerFlushMethods);

export default HVPUpdateScheduler;
